import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Building2, MoreHorizontal, Trash2, Globe } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

interface Vendor {
  id: string
  name: string
  category: string | null
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  website: string | null
  accountNumber: string | null
  pricingTier: string | null
  contractExpiry: string | null
  notes: string | null
  tags: string[]
  isActive: boolean
}

function AddVendorDialog() {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [category, setCategory] = useState('vendor')
  const [contactEmail, setContactEmail] = useState('')
  const [website, setWebsite] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/vendors', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      toast({ title: 'Vendor added', description: 'Vendor created successfully' })
      setOpen(false)
      setName(''); setContactEmail(''); setWebsite('')
    },
    onError: () => toast({ title: 'Error', description: 'Failed to create vendor', variant: 'destructive' }),
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> Add Vendor</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Vendor</DialogTitle>
          <DialogDescription>Add a new vendor or distributor.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Dicker Data" /></div>
          <div className="space-y-2">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="distributor">Distributor</SelectItem>
                <SelectItem value="vendor">Vendor</SelectItem>
                <SelectItem value="partner">Partner</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2"><Label>Contact Email</Label><Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="rep@vendor.com" /></div>
          <div className="space-y-2"><Label>Website</Label><Input value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://vendor.com" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={() => createMutation.mutate({ name, category, contactEmail: contactEmail || undefined, website: website || undefined })} disabled={!name || createMutation.isPending}>
            {createMutation.isPending ? 'Adding...' : 'Add Vendor'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function VendorsPage() {
  const [search, setSearch] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const { data, isLoading } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => apiClient.get<{ vendors: Vendor[]; pagination: unknown }>('/vendors'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/vendors/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
      toast({ title: 'Deleted', description: 'Vendor removed' })
    },
  })

  const vendors = data?.data?.vendors || []
  const filtered = vendors.filter((v) => v.name.toLowerCase().includes(search.toLowerCase()))

  const expiringCount = vendors.filter((v) => {
    if (!v.contractExpiry) return false
    const days = Math.ceil((new Date(v.contractExpiry).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    return days <= 30 && days > 0
  }).length

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Vendor Hub</h1>
          <p className="text-muted-foreground">Vendor and distributor relationship management</p>
        </div>
        <AddVendorDialog />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{vendors.length}</div><p className="text-xs text-muted-foreground">Total Vendors</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{vendors.filter(v => v.isActive).length}</div><p className="text-xs text-muted-foreground">Active</p></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-yellow-500">{expiringCount}</div><p className="text-xs text-muted-foreground">Contracts Expiring (30d)</p></CardContent></Card>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search vendors..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => <Card key={i}><CardHeader><Skeleton className="h-5 w-32" /></CardHeader><CardContent><Skeleton className="h-4 w-full" /></CardContent></Card>)}
        </div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="flex flex-col items-center py-12"><Building2 className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" /><p className="text-lg font-medium text-muted-foreground">No vendors found</p></CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((vendor) => (
            <Card key={vendor.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <CardTitle className="text-base font-medium">{vendor.name}</CardTitle>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {vendor.website && <DropdownMenuItem asChild><a href={vendor.website} target="_blank" rel="noopener noreferrer"><Globe className="mr-2 h-4 w-4" /> Visit Website</a></DropdownMenuItem>}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive" onClick={() => deleteMutation.mutate(vendor.id)}><Trash2 className="mr-2 h-4 w-4" /> Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-3">
                  {vendor.category && <Badge variant="outline">{vendor.category}</Badge>}
                  {vendor.pricingTier && <Badge variant="secondary">{vendor.pricingTier}</Badge>}
                </div>
                <div className="text-sm text-muted-foreground space-y-1">
                  {vendor.contactEmail && <p>{vendor.contactEmail}</p>}
                  {vendor.contactPhone && <p>{vendor.contactPhone}</p>}
                  {vendor.contractExpiry && <p>Contract expires: {new Date(vendor.contractExpiry).toLocaleDateString()}</p>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
